"use client";
import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  FlaskConical,
  Loader2,
  LogOut,
  Play,
  Terminal,
  Timer,
  XCircle,
} from "lucide-react";
import { useT } from "@/lib/i18n";
import { useApi } from "@/lib/hooks/use-api";
import { post } from "@/lib/api/client";
import { RequireAuth } from "@/components/auth";
import type { Lab } from "@/lib/types";
import type { LabHealth } from "@/lib/range/types";

const OS_LABELS: Record<string, string> = {
  kali: "Kali Linux",
  parrot: "Parrot OS",
  ubuntu: "Ubuntu 24.04",
};

function fmtTtl(min: number | null | undefined): string {
  if (min == null) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h > 0 ? `${h}ч ${m}м` : `${m}м`;
}

/**
 * Fullscreen-«комната» лаборатории: весь экран — рабочий стол студента
 * (noVNC), тонкий топбар со статусом/TTL/выходом. Отдельная изолированная
 * среда: у каждого студента свой run (своя сеть, свои порты, свой ticket).
 */
function RoomInner({ runId }: { runId: string }) {
  const t = useT();
  const router = useRouter();
  const [exiting, setExiting] = useState(false);
  const exitingRef = useRef(false);

  // Встроенный polling: пока не RUNNING — ждём (5с), RUNNING — TTL-обновление.
  const { data: lab } = useApi<Lab>(`/labs/${encodeURIComponent(runId)}`, 5000);
  const status = lab?.status;
  // Готовность УСЛУГ внутри контейнера (ttyd/noVNC): контейнер running ≠
  // desktop отвечает. Иначе iframe монтируется раньше времени и ловит
  // 502 «bad gateway: read ECONNRESET» — мёртвый экран до ручной перезагрузки.
  // services_ok: null — в шаблоне нет GUI/терминальных портов — не ждём.
  const { data: health } = useApi<LabHealth>(
    `/labs/${encodeURIComponent(runId)}/health`,
    3000,
  );
  const frameReady = health
    ? health.services_ok === true || health.services_ok === null
    : false;

  const doExit = useCallback(async () => {
    if (exitingRef.current) return;
    if (!window.confirm(t("room.exit_confirm"))) return;
    exitingRef.current = true;
    setExiting(true);
    try {
      await post(`/labs/${encodeURIComponent(runId)}/destroy`);
    } catch {
      /* lab может уже быть уничтожена (TTL) — уходим в любом случае */
    }
    document.exitFullscreen?.().catch(() => {});
    router.push("/dashboard");
  }, [runId, router, t]);

  /* Замок комнаты: browser-back/forward = та же процедура закрытия.
     Пока студент явно не закрыл лабораторию — из комнаты не выйти. */
  useEffect(() => {
    history.pushState({ cyberalemRoom: true }, "");
    const onPop = () => {
      if (exitingRef.current) return;
      if (window.confirm(t("room.exit_confirm"))) {
        void doExit();
      } else {
        history.pushState({ cyberalemRoom: true }, ""); // остаёмся в комнате
      }
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [doExit, t]);

  // Esc = выход (с подтверждением)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") void doExit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [doExit]);

  const denied = !lab; // 403/404 — useApi без данных

  const state: "preparing" | "ready" | "stopped" | "error" | "destroyed" =
    status === "RUNNING" || status === "PAUSED"
      ? "ready"
      : status === "STOPPED" || status === "STOPPING" || status === "EXPIRED"
        ? "stopped"
        : status === "ERROR"
          ? "error"
          : status === "DESTROYED"
            ? "destroyed"
            : "preparing";
  // Показываемый статус: RUNNING, но desktop ещё поднимается → «Подготовка»
  const shownState: typeof state =
    state === "ready" && !frameReady ? "preparing" : state;

  return (
    <div className="lab-room">
      <header className="lab-room-bar">
        <div className="lab-room-left">
          <FlaskConical size={18} className="lab-room-flask" />
          <span className="lab-room-title">
            {t("room.title")}
            <small>{OS_LABELS[lab?.os ?? "kali"] ?? "Linux"}</small>
          </span>
          <span className={`lab-room-status is-${shownState}`}>
            <span className="lab-room-dot" />
            {t(`room.status_${shownState}`)}
          </span>
        </div>
        <div className="lab-room-right">
          {shownState === "ready" && lab?.machines && lab.machines.length > 0 && (
            <span className="lab-room-machines" aria-label="Машини лабаратории">
              {lab.machines.map((m) => (
                <span key={m.name} className={`room-node role-${m.role}`}>
                  <span className="dot" aria-hidden="true" />
                  {m.name}
                  {m.ip && <span className="ip">{m.ip}</span>}
                </span>
              ))}
            </span>
          )}
          <span className="lab-room-ttl" title={t("room.ttl")}>
            <Timer size={14} />
            {fmtTtl(lab?.remaining_minutes)}
          </span>
          {lab?.terminal_url && (
            <a
              className="lab-room-terminal"
              href={lab.terminal_url}
              target="_blank"
              rel="noreferrer"
              title={t("room.terminal")}
            >
              <Terminal size={15} />
              {t("room.terminal")}
            </a>
          )}
          <button
            className="lab-room-exit"
            onClick={() => void doExit()}
            disabled={exiting}
          >
            {exiting ? <Loader2 size={15} className="spin" /> : <LogOut size={15} />}
            {t("room.exit")}
          </button>
        </div>
      </header>
      <main className="lab-room-body">
        {denied ? (
          <div className="lab-room-msg">
            <XCircle size={40} />
            <p>{t("room.denied")}</p>
            <Link className="button primary" href="/dashboard">
              {t("nav.dashboard")}
            </Link>
          </div>
        ) : state === "ready" && frameReady && lab?.workspace_url ? (
          <iframe
            className="lab-room-frame"
            src={lab.workspace_url}
            title={t("room.title")}
            allow="fullscreen"
          />
        ) : shownState === "preparing" ? (
          <div className="lab-room-msg">
            <Loader2 size={40} className="spin" />
            <p className="lab-room-prep-title">{t("room.preparing")}</p>
            <p className="muted">{t("room.preparing_hint")}</p>
          </div>
        ) : state === "stopped" ? (
          <div className="lab-room-msg">
            <XCircle size={40} />
            <p>{t("room.stopped_text")}</p>
            <button
              className="button primary"
              onClick={async () => {
                try {
                  await post(`/labs/${encodeURIComponent(runId)}/start`);
                } catch {
                  /* polling покажет актуальное состояние */
                }
              }}
            >
              <Play size={16} /> {t("room.start")}
            </button>
          </div>
        ) : state === "error" ? (
          <div className="lab-room-msg">
            <XCircle size={40} />
            <p>{t("room.error_text")}</p>
            {lab?.error && <code className="lab-room-error-detail">{lab.error}</code>}
            <Link className="button primary" href="/dashboard">
              {t("nav.dashboard")}
            </Link>
          </div>
        ) : (
          <div className="lab-room-msg">
            <XCircle size={40} />
            <p>{t("room.destroyed_text")}</p>
            <Link className="button primary" href="/dashboard">
              {t("nav.dashboard")}
            </Link>
          </div>
        )}
      </main>
    </div>
  );
}

export function LabRoomPage({ runId, children }: { runId: string; children?: ReactNode }) {
  void children;
  return (
    <RequireAuth>
      <RoomInner runId={runId} />
    </RequireAuth>
  );
}
